import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { getSizeLabel } from "../../../utils/getSizeLabel";
import api from "../../../services/api";

const EditableOrderItemCard = ({
  item,
  index,
  onItemChange,
  flavorOptions,
}) => {
  const [localItem, setLocalItem] = useState(item);

  const [sizeOptions, setSizeOptions] = useState([]);

  useEffect(() => {
    const fetchSizes = async () => {
      try {
        const res = await api.get(`/sizes?category=${item.category}`);
        setSizeOptions(res.data || []);
      } catch (err) {
        console.error(
          "Failed to fetch sizes for category:",
          item.category,
          err
        );
      }
    };

    if (item?.category) {
      fetchSizes();
    }
  }, [item.category]);

  useEffect(() => {
    onItemChange(localItem);
  }, [localItem]);

  const handleChange = (field, value) => {
    setLocalItem((prev) => {
      const updated = { ...prev };

      if (field === "flavor" && value?.name) {
        updated.flavor = value;
        updated.customizations = updateCustomizations(
          updated.customizations,
          "flavor",
          value.name
        );
      }

      if (field === "size" && value?._id) {
        const baseSizeName = getBaseSizeName(value); // e.g. "tall"
        if (!baseSizeName) return prev; // fallback

        updated.size = {
          _id: value._id,
          name: baseSizeName, // <-- override name here
          tierCount: value.tierCount,
        };

        updated.customizations = updateCustomizations(
          updated.customizations,
          "size",
          baseSizeName
        );
      } else if (field !== "flavor" && field !== "size") {
        updated[field] = value;
      }

      return updated;
    });
  };

  const getBaseSizeName = (sizeObj) => {
    return sizeObj?.displayName?.split(" ").pop().toLowerCase(); // fallback
  };

  const updateCustomizations = (list = [], key, newValue) => {
    const updated = [...list];
    const existing = updated.find((x) => x.key === key);
    if (existing) {
      existing.value = newValue;
    } else {
      updated.push({ key, value: newValue });
    }
    return updated;
  };

  const handleCustomizationChange = (key, value) => {
    setLocalItem((prev) => {
      const updated = { ...prev };
      const updatedCustomizations = [...(updated.customizations || [])];
      const existing = updatedCustomizations.find((x) => x.key === key);

      if (existing) {
        existing.value = value;
      } else {
        updatedCustomizations.push({ key, value });
      }

      updated.customizations = updatedCustomizations;
      return updated;
    });
  };

  const unitPrice = localItem.unitPrice || 0;
  const quantity = localItem.quantity || 1;
  const total = unitPrice * quantity;

  return (
    <Box
      p={2}
      border="1px solid #ccc"
      borderRadius={2}
      sx={{
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", md: "row" },
      }}>
      {item.image && (
        <Avatar
          variant="rounded"
          src={item.image}
          alt={item.displayName || item.name}
          sx={{ width: { xs: 200, sm: 400 }, height: { xs: 200, sm: 400 } }}
        />
      )}

      <Box flex={1}>
        <Typography fontWeight="bold" mb={0.5}>
          {item.displayName || item.name}
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Quantity"
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            disabled
            value={localItem.quantity}
            onChange={(e) =>
              handleChange("quantity", Math.max(1, Number(e.target.value)))
            }
          />

          <FormControl fullWidth>
            <InputLabel id={`size-label-${index}`}>Size</InputLabel>
            <Select
              labelId={`size-label-${index}`}
              label="Size"
              value={localItem.size?._id || ""}
              onChange={(e) => {
                const match = sizeOptions.find((s) => s._id === e.target.value);
                if (match) {
                  handleChange("size", match);
                }
              }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 400, mt: 0.5 } },
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              }}>
              {sizeOptions.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {getSizeLabel(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id={`flavor-label-${index}`}>Flavor</InputLabel>
            <Select
              labelId={`flavor-label-${index}`}
              label="Flavor"
              value={localItem.flavor?.name || ""}
              onChange={(e) =>
                handleChange("flavor", {
                  _id:
                    flavorOptions.find((f) => f.name === e.target.value)?._id ||
                    null,
                  name: e.target.value,
                })
              }
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 400, mt: 0.5 },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}>
              {flavorOptions.map((f) => (
                <MenuItem key={f._id} value={f.displayName}>
                  {f.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {item.customizations?.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">Customizations</Typography>
              <Stack spacing={1}>
                {item.customizations.map((c, i) => {
                  const existing =
                    localItem.customizations?.find((x) => x.key === c.key) ||
                    {};
                  return (
                    <TextField
                      key={i}
                      label={c.key}
                      fullWidth
                      value={existing.value || ""}
                      onChange={(e) =>
                        handleCustomizationChange(c.key, e.target.value)
                      }
                    />
                  );
                })}
              </Stack>
            </>
          )}

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Unit Price: ${unitPrice.toFixed(2)} × {quantity} ={" "}
              <strong>${total.toFixed(2)}</strong>
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default EditableOrderItemCard;
